import React from "react";
import { Link } from "react-router-dom";
import MoonbucksLogo from "../components/MoonbucksLogo";

export default function Terms() {
  return (
    <div className="min-h-screen bg-mb-cream text-neutral-900">
      <LegalHeader />
      <article className="max-w-3xl mx-auto px-6 lg:px-8 py-16 font-body">
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-2">Terms of Service</h1>
        <p className="text-sm text-neutral-500 mb-10">Last updated: February 14, 2026</p>

        <Section title="1. What Moonbucks is">
          Moonbucks (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is a subscription-based
          publisher of algorithmically-generated stock alerts. We scan U.S. equity markets, run a proprietary
          screener, and publish research notes and price levels for informational and educational purposes only.
        </Section>

        <Section title="2. NOT investment advice — the important part">
          Moonbucks is <strong>not</strong> a registered investment advisor, broker-dealer, financial planner,
          or fiduciary. Nothing published on this site, in our Discord, in our emails, or in our alerts
          constitutes investment advice, a recommendation to buy or sell any security, or an offer or
          solicitation of any kind. All content is for informational purposes only.
          <br /><br />
          Trading securities carries substantial risk. You can and may lose money — including your entire
          investment. Past performance of any alert, backtest, or track record we display is not
          indicative of future results. You are solely responsible for your own trading decisions.
          <br /><br />
          <strong>Consult a licensed financial advisor before making any investment decision.</strong>
        </Section>

        <Section title="3. Subscription &amp; billing">
          Access to premium alerts requires an active subscription. Prices are shown in USD and billed
          in advance on a recurring basis (weekly or monthly) via Stripe. Your subscription auto-renews
          until you cancel from your account page.
          <br /><br />
          You may cancel at any time; access continues through the end of the current paid period.
          Because we deliver time-sensitive information as it is generated, <strong>we do not
          refund partial or completed billing periods</strong>. If your renewal payment fails,
          Stripe will retry per its default schedule; if all retries fail, your subscription is
          canceled and access is revoked.
        </Section>

        <Section title="4. Your account">
          You are responsible for maintaining the confidentiality of your login credentials and for any
          activity under your account. Sharing an account with others, reselling access, or forwarding
          alerts to non-subscribers is prohibited and grounds for termination without refund.
        </Section>

        <Section title="5. Discord community">
          If you link your Discord account, we assign you a Subscriber role that grants access to
          private channels. When your subscription ends, the role is automatically revoked. Community
          conduct rules are posted in the server; violations may result in role revocation without refund.
        </Section>

        <Section title="6. Intellectual property">
          All alerts, research notes, backtest data, code, brand assets, and site content are the
          exclusive property of Moonbucks. You may use them for your personal, non-commercial trading
          decisions only. You may not republish, mirror, resell, or feed the content into any automated
          system or AI training dataset without our prior written consent.
        </Section>

        <Section title="7. Limitation of liability">
          To the maximum extent permitted by law, Moonbucks, its founders, employees, contractors, and
          affiliates shall not be liable for any indirect, incidental, special, consequential, or
          punitive damages, including but not limited to lost profits, lost trading gains, or losses
          resulting from any trade you place based on our alerts. Our total aggregate liability, in
          any circumstance, shall not exceed the amount you paid us in the twelve (12) months preceding
          the claim.
        </Section>

        <Section title="8. Termination">
          We may suspend or terminate your account at any time for violation of these terms, for
          suspected fraud or abuse, or at our sole discretion. Upon termination, your access ends
          immediately; no refund is owed for unused portions of a billing period previously commenced.
        </Section>

        <Section title="9. Changes to these terms">
          We may update these Terms from time to time. Material changes will be announced by email to
          your subscribed address. Continued use of the service after changes take effect constitutes
          acceptance of the revised Terms.
        </Section>

        <Section title="10. Governing law">
          These Terms are governed by the laws of the United States and the state in which Moonbucks
          is incorporated, without regard to conflict of law principles. Any dispute shall be resolved
          in the courts of that jurisdiction.
        </Section>

        <Section title="11. Contact">
          Questions? Email <a className="underline text-mb-emerald" href="mailto:hello@moonbucks.io">hello@moonbucks.io</a>.
        </Section>
      </article>
      <LegalFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-display text-xl font-semibold mb-3">{title}</h2>
      <div className="text-neutral-700 leading-relaxed">{children}</div>
    </section>
  );
}

function LegalHeader() {
  return (
    <header className="border-b border-mb-border bg-mb-cream/90 backdrop-blur sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="legal-brand-link">
          <div className="w-7 h-7 rounded-full bg-mb-emerald flex items-center justify-center text-white">
            <MoonbucksLogo className="w-4 h-4" />
          </div>
          <span className="font-serif-hero text-xl leading-none">Moonbucks</span>
        </Link>
        <div className="flex gap-4 text-sm">
          <Link to="/terms" className="text-neutral-700 hover:text-mb-emerald">Terms</Link>
          <Link to="/privacy" className="text-neutral-700 hover:text-mb-emerald">Privacy</Link>
        </div>
      </div>
    </header>
  );
}

function LegalFooter() {
  return (
    <footer className="border-t border-mb-border py-8 mt-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 text-xs text-neutral-500">
        © 2026 Moonbucks · Not investment advice · <Link to="/" className="underline">Home</Link>
      </div>
    </footer>
  );
}

export { LegalHeader, LegalFooter };
